import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect } from 'react'
import { CircleMarker, MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet'

const NEPAL: [number, number] = [28.2, 84.1]

function pin(label: string, off: boolean, selected: boolean) {
  const cls = `map-pin${off ? ' map-pin--off' : ''}${selected ? ' map-pin--on' : ''}`
  return L.divIcon({ className: '', html: `<span class="${cls}"><b>${label}</b></span>`, iconSize: [34, 34], iconAnchor: [17, 34] })
}

export interface StationPin {
  id: number
  lat: number
  lng: number
  /** Free connectors, shown on the pin. */
  label: string
  off: boolean
}

function Fit({ points, focus }: { points: [number, number][]; focus: [number, number] | null }) {
  const map = useMap()
  const key = points.map((p) => p.join(',')).join('|')
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 14)
    else if (points.length > 1) map.fitBounds(points, { padding: [40, 40], maxZoom: 13 })
    // Refit only when the set of stations changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key])
  useEffect(() => {
    if (focus) map.flyTo(focus, Math.max(map.getZoom(), 14), { duration: 0.6 })
  }, [map, focus])
  return null
}

/** Size changes (sheet opening, rotation) need Leaflet to re-measure. */
function Resize() {
  const map = useMap()
  useEffect(() => {
    const el = map.getContainer()
    const ro = new ResizeObserver(() => map.invalidateSize())
    ro.observe(el)
    return () => ro.disconnect()
  }, [map])
  return null
}

export function StationsMap({ pins, selectedId, onSelect, me, focus, className, line }: { pins: StationPin[]; selectedId?: number | null; onSelect?: (id: number) => void; me?: { lat: number; lng: number } | null; focus?: [number, number] | null; className?: string; line?: [number, number][] }) {
  const coords = line?.length ? [line[0], line[line.length - 1], ...pins.map((p) => [p.lat, p.lng] as [number, number])] : pins.map((p) => [p.lat, p.lng] as [number, number])
  return (
    <div className={className}>
      <MapContainer center={coords[0] ?? NEPAL} zoom={coords.length ? 12 : 7} zoomControl={false} style={{ height: '100%', width: '100%' }}>
        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" maxZoom={19} />
        <Fit points={coords} focus={focus ?? null} />
        <Resize />
        {line && line.length > 1 && <Polyline positions={line} pathOptions={{ color: '#2f6fd6', weight: 5, opacity: 0.85 }} />}
        {line && line.length > 1 && <CircleMarker center={line[line.length - 1]} radius={7} pathOptions={{ color: '#fff', weight: 3, fillColor: '#0f1c2e', fillOpacity: 1 }} />}
        {me && <CircleMarker center={[me.lat, me.lng]} radius={8} pathOptions={{ color: '#fff', weight: 3, fillColor: '#2f6fd6', fillOpacity: 1 }} />}
        {pins.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pin(p.label, p.off, p.id === selectedId)} zIndexOffset={p.id === selectedId ? 1000 : 0} eventHandlers={onSelect ? { click: () => onSelect(p.id) } : undefined} />
        ))}
      </MapContainer>
    </div>
  )
}
